import { useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import logo from "../../assets/kangaroo/intellifenceLogo.png";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import LoginService from "../../services/LoginService";
import {
  ShieldCheck,
  Lock,
  Fingerprint,
  Eye,
  EyeOff,
  ArrowLeft,
  KeyRound,
} from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordShow, setPasswordShow] = useState(false);

  // Attempt to grab email from navigation state if it was passed from ForgotPassword
  const initialEmail = location.state?.email || "";

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Emailis required"),
    otp: Yup.string()
      .matches(/^[0-9]+$/, "OTP must be only digits")
      .required("OTP is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Please confirm your password"),
  });

  const handleReset = async (values, { setSubmitting }) => {
    try {
      // Convert otp to a number to match the integer($int32) API requirement
      const numericOtp = parseInt(values.otp, 10);
      await LoginService.resetpassword(
        values.email,
        numericOtp,
        values.password,
      );
      toast.success("Password reset successfully! Please login.");
      navigate("/login");
    } catch (error) {
      toast.error("Invalid OTP or request failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="vh-100 overflow-hidden p-0 bg-light">
      <Row className="g-0 h-100">
        {/* LEFT PANEL */}
        <Col
          lg={7}
          className="d-none d-lg-flex flex-column justify-content-center align-items-center text-center text-white p-5"
          style={{
            background: "linear-gradient(135deg, #764ba2 0%, #c77dff 100%)",
          }}
        >
          <img
            src={logo}
            alt="Kangaroo Logo"
            className="img-fluid mb-4"
            style={{ maxHeight: "140px" }}
          />

          {/* <div>
                     <h1
                       className="m-0 p-0 font-level-4  text-uppercase"
                       style={{ letterSpacing: "3px", lineHeight: "1" }}
                     >
                       Start Smart
                     </h1>
         
                     <h1
                       className="font-level-4 fw-bold text-uppercase mt-0"
                       style={{
                         letterSpacing: "4px",
                         color: "#7E1790",
                         lineHeight: "1",
                       }}
                     >
                       With Kangaroo
                     </h1>
                   </div> */}
          <hr
            className="w-25 my-1"
            style={{
              backgroundColor: "#F6921E",
              height: "3px",
              border: "none",
              opacity: 1,
            }}
          />

          <Card className="bg-transparent border-0 w-75">
            <CardBody className="p-4">
              <h3 className="fw-bold mb-3 text-white">
                Where Structured Information Empowers you to Start Smart and
                Deliver with Precision.
              </h3>
              {/* <p className="fs-5 mb-0 text-white">
                        Where Structured Information Empowers You To Start Smart and Deliver with Precision.
                       </p> */}
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT PANEL */}
        <Col xs={12} lg={5} className="d-flex flex-column h-100 bg-white">
          <div className="flex-grow-1 d-flex align-items-center justify-content-center p-4 p-md-5 overflow-auto">
            <Card className="border-0 w-100" style={{ maxWidth: "420px" }}>
              <CardBody className="p-0">
                <div className="text-center mb-4">
                  <div
                    className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      width: "70px",
                      height: "70px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    }}
                  >
                     <KeyRound className="text-white" size={30} />
                  </div>
                  <h2 className="fw-bold text-dark mb-1">Reset Password</h2>
                  <p className="text-muted small">
                    Verification code sent to your inbox
                  </p>
                </div>

                <Formik
                  initialValues={{
                    email: initialEmail,
                    otp: "",
                    password: "",
                    confirmPassword: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleReset}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit} noValidate>
                      {/* Email (Hidden or Read-only if passed, but required by your API) */}
                      <FormGroup className="form-floating mb-3">
                        <Input
                          name="email"
                          placeholder="Email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={touched.email && !!errors.email}
                          className="rounded-3 border-2 shadow-sm"
                        />
                        <Label>Email</Label>
                        <FormFeedback>{errors.email}</FormFeedback>
                      </FormGroup>

                      {/* OTP Field */}
                      <FormGroup className="form-floating mb-3">
                        <Input
                          name="otp"
                          placeholder="Enter OTP"
                          value={values.otp}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={touched.otp && !!errors.otp}
                          className="rounded-3 border-2 fw-bold text-center shadow-sm"
                          style={{ letterSpacing: "4px", fontSize: "1.2rem" }}
                        />
                        <Label>Verification Code (OTP)</Label>
                        <FormFeedback>{errors.otp}</FormFeedback>
                      </FormGroup>

                      {/* New Password */}
                      <FormGroup className="form-floating mb-3 position-relative">
                        <Input
                          type={passwordShow ? "text" : "password"}
                          name="password"
                          placeholder="New Password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={touched.password && !!errors.password}
                          className="rounded-3 border-2 shadow-sm"
                        />
                        <Label>New Password</Label>
                        <Button
                          color="link"
                          className="position-absolute top-50 end-0 translate-middle-y text-muted border-0 bg-transparent p-0 pe-3"
                          onClick={() => setPasswordShow(!passwordShow)}
                          type="button"
                          style={{ zIndex: 10 }}
                        >
                          {passwordShow ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </Button>
                        <FormFeedback>{errors.password}</FormFeedback>
                      </FormGroup>

                      {/* Confirm Password */}
                      <FormGroup className="form-floating mb-4">
                        <Input
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm Password"
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={
                            touched.confirmPassword && !!errors.confirmPassword
                          }
                          className="rounded-3 border-2 shadow-sm"
                        />
                        <Label>Confirm New Password</Label>
                        <FormFeedback>{errors.confirmPassword}</FormFeedback>
                      </FormGroup>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-100 py-2 fw-bold shadow-sm mb-3"
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                          height: "50px",
                          borderRadius: "10px",
                        }}
                      >
                        {isSubmitting ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          "Reset Password"
                        )}
                      </Button>

                      <div className="text-center">
                        <Link
                          to="/login"
                          className="text-decoration-none small fw-semibold text-muted d-flex align-items-center justify-content-center gap-1"
                        >
                          <ArrowLeft size={14} /> Back to Sign In
                        </Link>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </div>

          <div className="py-3 border-top bg-light text-center flex-shrink-0 w-100">
            <small className="text-muted d-flex align-items-center justify-content-center gap-2">
              <ShieldCheck size={14} className="text-success" />
              Security Protocol Active
            </small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
